import { type TouchEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, MessageCircle, Phone, ShoppingBag, ShoppingCart, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { CartItem } from '@/features/cart/types';

interface CartBottomSheetProps {
  open: boolean;
  items: CartItem[];
  itemCount: number;
  subtotal: number;
  total: number;
  deliveryFee: number;
  cartHref?: string;
  phoneNumber?: string;
  userRole?: string;
  accountActionLabel?: string;
  onClose: () => void;
  onTextOrder: () => void;
  onCallOrder: () => void;
  onAccountAction?: () => void;
  onAdminClick?: () => void;
  onRiderClick?: () => void;
  onSupportCall?: () => void;
  onWhatsAppInquiry?: () => void;
}

function formatMoney(value: number): string {
  return `KES ${value.toLocaleString()}`;
}

function SheetHandle({
  onTouchStart,
  onTouchMove,
  onTouchEnd,
}: {
  onTouchStart: (event: TouchEvent<HTMLDivElement>) => void;
  onTouchMove: (event: TouchEvent<HTMLDivElement>) => void;
  onTouchEnd: () => void;
}) {
  return (
    <div
      className="flex items-center justify-center px-6 pb-3 pt-3"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <div className="h-1.5 w-14 rounded-full bg-brand-200" />
    </div>
  );
}

export function CartBottomSheet({
  open,
  items,
  itemCount,
  subtotal,
  total,
  deliveryFee,
  cartHref = '/cart',
  phoneNumber,
  userRole,
  accountActionLabel,
  onClose,
  onTextOrder,
  onCallOrder,
  onAccountAction,
  onAdminClick,
  onRiderClick,
  onSupportCall,
  onWhatsAppInquiry,
}: CartBottomSheetProps) {
  const [isVisible, setIsVisible] = useState(open);
  const closeTimeoutRef = useRef<number | null>(null);
  const touchStartYRef = useRef<number | null>(null);
  const touchCurrentYRef = useRef<number | null>(null);

  useEffect(() => {
    if (closeTimeoutRef.current) {
      window.clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }

    if (open) {
      setIsVisible(true);
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }

    document.body.style.overflow = '';
    closeTimeoutRef.current = window.setTimeout(() => {
      setIsVisible(false);
    }, 240);

    return () => {
      document.body.style.overflow = '';
      if (closeTimeoutRef.current) {
        window.clearTimeout(closeTimeoutRef.current);
      }
    };
  }, [open]);

  const estimatedSavings = useMemo(() => {
    if (deliveryFee <= 0) {
      return 'Delivery has been waived on this cart.';
    }

    return `Delivery: ${formatMoney(deliveryFee)}`;
  }, [deliveryFee]);

  if (!isVisible) {
    return null;
  }

  const quickActions = [
    (userRole === 'super-admin' || userRole === 'admin') && onAdminClick
      ? {
          label: 'Admin Dashboard',
          onClick: onAdminClick,
        }
      : null,
    userRole === 'rider' && onRiderClick
      ? {
          label: 'Rider Portal',
          onClick: onRiderClick,
        }
      : null,
    onAccountAction && accountActionLabel
      ? {
          label: accountActionLabel,
          onClick: onAccountAction,
        }
      : null,
    onSupportCall
      ? {
          label: phoneNumber ? `Call ${phoneNumber}` : 'Call Support',
          onClick: onSupportCall,
        }
      : null,
    onWhatsAppInquiry
      ? {
          label: 'WhatsApp Support',
          onClick: onWhatsAppInquiry,
        }
      : null,
  ].filter(Boolean) as Array<{ label: string; onClick: () => void }>;

  const handleTouchStart = (event: TouchEvent<HTMLDivElement>) => {
    touchStartYRef.current = event.touches[0]?.clientY ?? null;
    touchCurrentYRef.current = touchStartYRef.current;
  };

  const handleTouchMove = (event: TouchEvent<HTMLDivElement>) => {
    touchCurrentYRef.current = event.touches[0]?.clientY ?? null;
  };

  const handleTouchEnd = () => {
    if (
      touchStartYRef.current !== null &&
      touchCurrentYRef.current !== null &&
      touchCurrentYRef.current - touchStartYRef.current > 80
    ) {
      onClose();
    }

    touchStartYRef.current = null;
    touchCurrentYRef.current = null;
  };

  return (
    <div className="fixed inset-0 z-50" aria-modal="true" role="dialog">
      <button
        type="button"
        className={cn(
          'absolute inset-0 bg-brand-950/42 transition-opacity duration-200',
          open ? 'opacity-100' : 'opacity-0'
        )}
        onClick={onClose}
        aria-label="Close cart actions"
      />

      <div
        className={cn(
          'absolute inset-x-0 bottom-0 mx-auto w-full max-w-2xl rounded-t-[2rem] border border-brand-100 bg-white shadow-2xl shadow-brand-950/18',
          'transition-transform duration-300 ease-out',
          open ? 'translate-y-0' : 'translate-y-full'
        )}
      >
        <SheetHandle
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        />

        <div className="flex max-h-[78vh] flex-col px-4 pb-[calc(env(safe-area-inset-bottom,0px)+1rem)] sm:px-6">
          <div className="mb-4 flex items-start justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-500">
                Order Actions
              </p>
              <h2 className="mt-1 text-xl font-display font-semibold text-brand-950">
                Your cart is ready
              </h2>
              <p className="mt-1 text-sm text-brand-600">
                {itemCount} item{itemCount === 1 ? '' : 's'} selected. Review or finish ordering from here.
              </p>
            </div>
            <Button type="button" variant="ghost" size="icon" onClick={onClose} aria-label="Close cart sheet">
              <ChevronDown className="h-5 w-5" />
            </Button>
          </div>

          <div className="flex-1 space-y-4 overflow-y-auto pb-4">
            <div className="rounded-2xl border border-brand-100 bg-brand-50 p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-brand-700 shadow-sm">
                    <ShoppingCart className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-brand-950">Cart summary</p>
                    <p className="text-sm text-brand-600">{estimatedSavings}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs uppercase tracking-wide text-brand-500">Total</p>
                  <p className="text-lg font-semibold text-brand-950">{formatMoney(total || subtotal)}</p>
                </div>
              </div>

              <div className="mt-4 space-y-2">
                {items.slice(0, 4).map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between gap-3 rounded-xl bg-white px-3 py-2"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-brand-950">{item.product.name}</p>
                      <p className="text-xs text-brand-500">
                        Qty {item.quantity}
                        {item.variant?.volume ? ` • ${item.variant.volume}` : ''}
                      </p>
                    </div>
                    <p className="text-sm font-semibold text-brand-900">
                      {formatMoney(item.unitPrice * item.quantity)}
                    </p>
                  </div>
                ))}
                {items.length > 4 && (
                  <p className="px-1 text-xs text-brand-500">
                    +{items.length - 4} more item{items.length - 4 === 1 ? '' : 's'} in your cart
                  </p>
                )}
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <Link to={cartHref} onClick={onClose}>
                <Button variant="outline" fullWidth className="justify-center">
                  <ShoppingBag className="h-4 w-4" />
                  View Cart
                </Button>
              </Link>
              <Button onClick={onTextOrder} fullWidth className="bg-green-600 hover:bg-green-700">
                <MessageCircle className="h-4 w-4" />
                Text to Order
              </Button>
              <Button onClick={onCallOrder} fullWidth className="bg-amber-500 hover:bg-amber-600 text-white">
                <Phone className="h-4 w-4" />
                Call to Order
              </Button>
            </div>

            {quickActions.length > 0 && (
              <div className="rounded-2xl border border-brand-100 bg-white p-4">
                <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-brand-900">
                  <User className="h-4 w-4 text-brand-500" />
                  More actions
                </div>
                <div className="grid gap-2 sm:grid-cols-2">
                  {quickActions.map((action) => (
                    <Button
                      key={action.label}
                      type="button"
                      variant="ghost"
                      className="justify-start rounded-2xl border border-brand-100 bg-brand-50 px-4 text-left"
                      onClick={() => {
                        action.onClick();
                        onClose();
                      }}
                    >
                      {action.label}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            <div className="rounded-2xl border border-dashed border-brand-200 bg-brand-50 p-4 text-sm text-brand-600">
              Order actions open the same existing checkout and share flows, now tucked behind the cart so the page stays calmer while people browse.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CartBottomSheet;
